import Decimal from 'decimal.js';

/**
 * P21 B6 – Tính lương (docs/phases/P21.md §4.6, §9.5). Logic thuần, server là nguồn sự thật; client chỉ xem trước.
 * - Từng khoản làm tròn tới đồng; riêng THỰC LĨNH làm tròn tới nghìn (ROUND_HALF_UP) – anh Tiến chốt L1.
 * - "Hỗ trợ giá thị trường tăng" = tỷ lệ × tổng các khoản gốc (số tự tính, không theo số sửa tay) – như minhtiencrm (L5 = A).
 * - Khoản có cờ "mất khi vắng không phép" (Lương chuyên cần) = 0 khi mất chuyên cần (DEC-265: ≥ 1 ngày KP hoặc ≥ 3 lần trễ không đơn).
 * - BH NV: BHXH 8% + BHYT 1,5% trên min(lương BH, trần BHXH); BHTN 1% trên min(lương BH, trần BHTN).
 * - TNCN: lũy tiến theo biểu tham số; flat10 = 10% × (gross − miễn thuế) khi ≥ 2.000.000.
 */

const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });
type Num = number | string | Decimal;
const dec = (v: Num | null | undefined) => new D(v ?? 0);
/** Làm tròn tới đồng (ROUND_HALF_UP). */
export const roundVnd = (v: Num) => dec(v).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber();
/** Làm tròn tới nghìn đồng (ROUND_HALF_UP) – chỉ dùng cho thực lĩnh. */
export const roundThousand = (v: Num) => dec(v).div(1000).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).mul(1000).toNumber();

export const SALARY_ITEM_KINDS = ['earning', 'deduction'] as const;
export type SalaryItemKind = (typeof SALARY_ITEM_KINDS)[number];
export const SALARY_ITEM_KIND_LABELS: Record<SalaryItemKind, string> = { earning: 'Cộng', deduction: 'Trừ' };

export const SALARY_CALCS = ['fixed', 'by_workday', 'by_hour', 'manual', 'formula'] as const;
export type SalaryCalc = (typeof SALARY_CALCS)[number];
export const SALARY_CALC_LABELS: Record<SalaryCalc, string> = {
  fixed: 'Cố định/tháng',
  by_workday: 'Theo ngày công',
  by_hour: 'Giờ × đơn giá',
  manual: 'Nhập tay mỗi kỳ',
  formula: 'Công thức (tỷ lệ × các khoản)',
};

/** Nguồn tự động: lương HĐ từ hồ sơ lương, tiền tài xế từ chấm công tài xế, BH NV do bảng lương tính. */
export const SALARY_AUTO_SOURCES = ['contract_salary', 'driver_ot', 'driver_late', 'insurance', 'driver_trip', 'salary_advance', 'sunday', 'after_hours', 'bonus', 'recovery', 'fuel'] as const;
export type SalaryAutoSource = (typeof SALARY_AUTO_SOURCES)[number];
export const SALARY_AUTO_SOURCE_LABELS: Record<SalaryAutoSource, string> = {
  contract_salary: 'Lương HĐ trong hồ sơ lương',
  driver_ot: 'Tự động từ chấm công tài xế (tăng ca)',
  driver_late: 'Tự động từ chấm công tài xế (đi trễ, quên trả xe)',
  driver_trip: 'Tự động từ chuyến tỉnh đã duyệt (công tác phí + lưu trú)',
  insurance: 'Bảng lương tự tính BHXH-BHYT-BHTN',
  salary_advance: 'Tự động: tổng tạm ứng đã duyệt của tháng (Cổng nhân viên)',
  sunday: 'Tự động: công Chủ nhật thực tế (vd 0,77) × đơn giá ngày',
  after_hours: 'Tự động: giờ ngoài giờ TP đã xác nhận × đơn giá giờ',
  bonus: 'Tự động: thưởng cuối tháng Admin / GĐ đã nhập',
  fuel: 'Tự động: hỗ trợ xăng xe đi công trình theo báo cáo có ảnh + GPS (DEC-324)',
  recovery: 'Tự động: khoản thu hồi chi dư GĐ đã duyệt (màn Đối soát)',
};

/** PR 7 (DEC-270): mã khoản tăng ca theo giờ; đơn giá thấp hơn 150% lương giờ thì cảnh báo (Bộ luật LĐ Đ98), không chặn. */
export const OT_MIN_RATE = 1.5;
/** Làm Chủ nhật (ngày nghỉ hằng tuần): tối thiểu 200% lương ngày. */
export const SUNDAY_MIN_RATE = 2;
/** Giờ làm 1 ngày để quy lương giờ */
export const HOURS_PER_DAY = 8;

/** Ngoài giờ: ở lại sau giờ ra ca từ 30 phút trở lên mới đưa TP xác nhận; giờ đề xuất làm tròn xuống 0,5 giờ. */
export const AFTER_HOURS_MIN_MINUTES = 30;
export function suggestAfterHours(minutes: number): number {
  if (!(minutes >= AFTER_HOURS_MIN_MINUTES)) return 0;
  return Math.floor(minutes / 30) / 2;
}

/** Lương giờ = lương HĐ ÷ công chuẩn ÷ 8 (làm tròn đồng). */
export function hourlyWage(contractSalary: number, stdDays: number): number {
  if (!(stdDays > 0)) return 0;
  return roundVnd(dec(contractSalary).div(stdDays).div(HOURS_PER_DAY));
}

/**
 * Cảnh báo đơn giá tăng ca / ngoài giờ / Chủ nhật thấp hơn mức luật (D5: giữ đơn giá CRM, chỉ cảnh báo).
 * `items` = các khoản trên dòng lương (code, autoSource, calc, rate, qty).
 */
export function overtimeRateWarnings(
  items: readonly { name: string; calc: string; autoSource: string | null; rate: number | null; qty: number | null }[],
  contractSalary: number,
  stdDays: number,
): string[] {
  if (!(stdDays > 0) || !(contractSalary > 0)) return [];
  const day = dec(contractSalary).div(stdDays);
  const out: string[] = [];
  const fmt = (n: number) => n.toLocaleString('vi-VN');
  for (const it of items) {
    if (!it.qty || it.rate === null || it.calc !== 'by_hour') continue;
    if (it.autoSource === 'sunday') {
      const min = roundVnd(day.mul(SUNDAY_MIN_RATE));
      if (it.rate < min) out.push(`${it.name}: đơn giá ${fmt(it.rate)} đ/ngày thấp hơn 200% lương ngày (${fmt(min)} đ)`);
    } else if (it.autoSource === null || it.autoSource === 'after_hours') {
      const min = roundVnd(day.div(HOURS_PER_DAY).mul(OT_MIN_RATE));
      if (it.rate < min) out.push(`${it.name}: đơn giá ${fmt(it.rate)} đ/giờ thấp hơn 150% lương giờ (${fmt(min)} đ)`);
    }
  }
  return out;
}

export const TAX_MODES = ['progressive', 'flat10', 'none'] as const;
export type TaxMode = (typeof TAX_MODES)[number];
export const TAX_MODE_LABELS: Record<TaxMode, string> = { progressive: 'Lũy tiến', flat10: '10% cố định', none: 'Không tính thuế' };

export const PAYROLL_STATUSES = ['draft', 'calculated', 'pending', 'approved'] as const;
export type PayrollStatus = (typeof PAYROLL_STATUSES)[number];
export const PAYROLL_STATUS_LABELS: Record<PayrollStatus, string> = {
  draft: 'Nháp',
  calculated: 'Đã tính',
  pending: 'Chờ duyệt',
  approved: 'Đã duyệt · khóa',
};

/** Bậc thuế: `upTo` = mức trên của thu nhập tính thuế (cộng dồn), null = không giới hạn. */
export interface TaxBracket {
  upTo: number | null;
  rate: number;
}

export interface PayrollParams {
  empSocialRate: number;
  empHealthRate: number;
  empUnemploymentRate: number;
  erSocialRate: number;
  erHealthRate: number;
  erUnemploymentRate: number;
  erUnionRate: number;
  socialCap: number;
  unemploymentCap: number;
  selfDeduction: number;
  dependantDeduction: number;
  flatTaxRate: number;
  flatTaxThreshold: number;
  taxBrackets: TaxBracket[];
}

/** Mức nạp sẵn – CẦN KẾ TOÁN KIỂM (P21 §4.6); 7 bậc 5–35%. */
export const DEFAULT_TAX_BRACKETS: TaxBracket[] = [
  { upTo: 5_000_000, rate: 0.05 },
  { upTo: 10_000_000, rate: 0.1 },
  { upTo: 18_000_000, rate: 0.15 },
  { upTo: 32_000_000, rate: 0.2 },
  { upTo: 52_000_000, rate: 0.25 },
  { upTo: 80_000_000, rate: 0.3 },
  { upTo: null, rate: 0.35 },
];

export const DEFAULT_PAYROLL_PARAMS: PayrollParams = {
  empSocialRate: 0.08,
  empHealthRate: 0.015,
  empUnemploymentRate: 0.01,
  erSocialRate: 0.175,
  erHealthRate: 0.03,
  erUnemploymentRate: 0.01,
  erUnionRate: 0.02,
  socialCap: 46_800_000,
  unemploymentCap: 106_200_000,
  selfDeduction: 15_500_000,
  dependantDeduction: 6_200_000,
  flatTaxRate: 0.1,
  flatTaxThreshold: 2_000_000,
  taxBrackets: DEFAULT_TAX_BRACKETS,
};

/** Kiểm biểu thuế: tăng dần, bậc cuối không giới hạn, thuế suất 0–1. Trả về thông báo lỗi hoặc null. */
export function validateTaxBrackets(b: TaxBracket[]): string | null {
  if (!b.length) return 'Biểu thuế phải có ít nhất 1 bậc';
  for (let i = 0; i < b.length; i++) {
    const x = b[i]!;
    if (!(x.rate >= 0 && x.rate <= 1)) return `Bậc ${i + 1}: thuế suất phải từ 0 đến 100%`;
    const last = i === b.length - 1;
    if (last && x.upTo !== null) return 'Bậc cuối phải để trống mức trên (không giới hạn)';
    if (!last && (x.upTo === null || x.upTo <= 0)) return `Bậc ${i + 1}: nhập mức trên`;
    if (i > 0 && !last && x.upTo! <= b[i - 1]!.upTo!) return `Bậc ${i + 1}: mức trên phải lớn hơn bậc trước`;
  }
  return null;
}

/** Thuế TNCN lũy tiến từng phần (làm tròn tới đồng). */
export function progressiveTax(taxable: Num, brackets: TaxBracket[]): number {
  let rest = Decimal.max(dec(taxable), 0);
  let prev = new D(0);
  let tax = new D(0);
  for (const b of brackets) {
    if (rest.lte(0)) break;
    const width = b.upTo === null ? rest : Decimal.min(rest, dec(b.upTo).minus(prev));
    tax = tax.plus(width.mul(b.rate));
    rest = rest.minus(width);
    if (b.upTo !== null) prev = dec(b.upTo);
  }
  return roundVnd(tax);
}

export interface InsuranceResult {
  base: number;
  employee: { social: number; health: number; unemployment: number; total: number };
  employer: { social: number; health: number; unemployment: number; union: number; total: number };
}

/** BH NV + BH DN + KPCĐ theo trần. Không tham gia BH → toàn 0. */
export function computeInsurance(base: Num, params: PayrollParams, hasInsurance: boolean): InsuranceResult {
  const b = Decimal.max(dec(base), 0);
  if (!hasInsurance || b.isZero()) {
    return { base: roundVnd(b), employee: { social: 0, health: 0, unemployment: 0, total: 0 }, employer: { social: 0, health: 0, unemployment: 0, union: 0, total: 0 } };
  }
  const s = Decimal.min(b, params.socialCap);
  const u = Decimal.min(b, params.unemploymentCap);
  const e = { social: roundVnd(s.mul(params.empSocialRate)), health: roundVnd(s.mul(params.empHealthRate)), unemployment: roundVnd(u.mul(params.empUnemploymentRate)) };
  const r = {
    social: roundVnd(s.mul(params.erSocialRate)),
    health: roundVnd(s.mul(params.erHealthRate)),
    unemployment: roundVnd(u.mul(params.erUnemploymentRate)),
    union: roundVnd(s.mul(params.erUnionRate)),
  };
  return {
    base: roundVnd(b),
    employee: { ...e, total: e.social + e.health + e.unemployment },
    employer: { ...r, total: r.social + r.health + r.unemployment + r.union },
  };
}

// ---------------------------------------------------------------------------
// Một dòng lương
// ---------------------------------------------------------------------------
export interface PayrollItemDef {
  itemId: number | null;
  code: string;
  name: string;
  kind: SalaryItemKind;
  calc: SalaryCalc;
  autoSource: SalaryAutoSource | null;
  taxable: boolean;
  /** Mức miễn thuế tối đa của khoản không chịu thuế; null = miễn toàn bộ */
  taxExemptCap: number | null;
  countsForInsurance: boolean;
  loseOnUnexcusedAbsence: boolean;
  /** Khoản công thức: tỷ lệ × tổng các mã gốc */
  formulaRate: number | null;
  formulaBaseCodes: string[];
  sortOrder: number;
}

export interface PayrollItemInput {
  def: PayrollItemDef;
  /** Giá trị gán (tiền/tháng; đơn giá với khoản theo giờ). Khoản tự động bỏ qua. */
  value: number;
  /** Số sửa tay đang giữ (null = dùng số tự tính) */
  override?: number | null;
  overrideReason?: string | null;
}

export interface PayrollLineInput {
  stdDays: number;
  workDays: number;
  otHours: number;
  unexcusedAbsenceDays: number;
  /** DEC-265: mất chuyên cần (≥ 1 ngày KP hoặc ≥ 3 lần trễ / về sớm không đơn, đã bỏ vi phạm được chấp nhận). Không truyền → theo ngày KP */
  diligenceLost?: boolean;
  contractSalary: number;
  /** null = lương HĐ + các khoản có cờ tính BH */
  insuranceSalary: number | null;
  dependants: number;
  taxMode: TaxMode;
  hasInsurance: boolean;
  driverOt: number;
  driverLate: number;
  /** PR5: công tác phí + lưu trú chuyến tỉnh (khoản "Công tác phí tài xế") */
  driverTrip?: number;
  /** P21 PR 4: tổng tạm ứng đã duyệt của tháng (khoản "Đã tạm ứng") */
  salaryAdvance?: number;
  /** PR 8b: tổng thu hồi chi dư GĐ đã duyệt, trừ vào kỳ này */
  recovery?: number;
  /** Công Chủ nhật thực tế (khoản nguồn "sunday"), vd 0,77; lệnh CN không chấm giờ = 1 */
  sundayDays?: number;
  /** PR 7: giờ ngoài giờ TP đã xác nhận (khoản nguồn "after_hours") */
  afterHours?: number;
  /** PR 7: tổng thưởng cuối tháng (khoản nguồn "bonus") */
  bonus?: number;
  /** DEC-324: tổng hỗ trợ xăng xe đi công trình của tháng (khoản nguồn "fuel") */
  fuelSupport?: number;
  items: PayrollItemInput[];
}

export interface PayrollItemResult {
  itemId: number | null;
  code: string;
  name: string;
  kind: SalaryItemKind;
  calc: SalaryCalc;
  autoSource: SalaryAutoSource | null;
  taxable: boolean;
  taxExemptCap: number | null;
  sortOrder: number;
  qty: number | null;
  rate: number | null;
  autoAmount: number;
  overrideAmount: number | null;
  overrideReason: string | null;
  amount: number;
}

export interface PayrollTotals {
  gross: number;
  taxExempt: number;
  insuranceEmployee: number;
  taxableIncome: number;
  pit: number;
  deductions: number;
  net: number;
  insuranceEmployer: number;
  employerCost: number;
}

export interface PayrollLineResult extends PayrollTotals {
  ratio: number;
  insuranceSalary: number;
  insurance: InsuranceResult;
  items: PayrollItemResult[];
}

/** Tỷ lệ công = min(1, công thực / công chuẩn); 4 chữ số lẻ để hiển thị, tính dùng Decimal đủ. */
export function workRatio(workDays: number, stdDays: number): Decimal {
  if (!(stdDays > 0)) return new D(0);
  return Decimal.min(1, Decimal.max(0, dec(workDays).div(stdDays)));
}

/** Tổng của 1 dòng lương từ số tiền các khoản (đã gồm số sửa tay). */
export function computeTotals(
  items: Pick<PayrollItemResult, 'kind' | 'autoSource' | 'taxable' | 'taxExemptCap' | 'amount'>[],
  o: { taxMode: TaxMode; dependants: number; params: PayrollParams; insuranceEmployee: number; insuranceEmployer: number },
): PayrollTotals {
  let gross = new D(0);
  let exempt = new D(0);
  let deductions = new D(0);
  let insEmp: Decimal | null = null;
  for (const it of items) {
    const a = dec(it.amount);
    if (it.autoSource === 'insurance') {
      insEmp = (insEmp ?? new D(0)).plus(a);
      continue;
    }
    if (it.kind === 'earning') {
      gross = gross.plus(a);
      if (!it.taxable) exempt = exempt.plus(it.taxExemptCap === null ? Decimal.max(a, 0) : Decimal.max(0, Decimal.min(a, it.taxExemptCap)));
    } else {
      deductions = deductions.plus(a);
    }
  }
  const ins = insEmp ?? dec(o.insuranceEmployee);
  const p = o.params;
  let taxable = new D(0);
  let pit = 0;
  if (o.taxMode === 'progressive') {
    taxable = Decimal.max(0, gross.minus(exempt).minus(ins).minus(p.selfDeduction).minus(dec(p.dependantDeduction).mul(o.dependants)));
    pit = progressiveTax(taxable, p.taxBrackets);
  } else if (o.taxMode === 'flat10') {
    taxable = Decimal.max(0, gross.minus(exempt));
    pit = taxable.gte(p.flatTaxThreshold) ? roundVnd(taxable.mul(p.flatTaxRate)) : 0;
  }
  const net = roundThousand(gross.minus(ins).minus(pit).minus(deductions));
  return {
    gross: roundVnd(gross),
    taxExempt: roundVnd(exempt),
    insuranceEmployee: roundVnd(ins),
    taxableIncome: roundVnd(taxable),
    pit,
    deductions: roundVnd(deductions),
    net,
    insuranceEmployer: o.insuranceEmployer,
    employerCost: roundVnd(gross.plus(o.insuranceEmployer)),
  };
}

/** Tính 1 dòng lương: số tự tính từng khoản → áp số sửa tay → tổng. */
/** Khoản chuyên cần bị mất: theo cờ mất chuyên cần nếu có, không thì theo ngày vắng không phép */
const loses = (input: PayrollLineInput) => input.diligenceLost ?? input.unexcusedAbsenceDays > 0;

export function calculatePayrollLine(input: PayrollLineInput, params: PayrollParams): PayrollLineResult {
  const ratio = workRatio(input.workDays, input.stdDays);
  const sorted = [...input.items].sort((a, b) => a.def.sortOrder - b.def.sortOrder || a.def.code.localeCompare(b.def.code));

  // Lương đóng BH: số trong hồ sơ, trống = lương HĐ + khoản có cờ tính BH (giá trị tháng, không theo công)
  let insBase = dec(input.insuranceSalary ?? 0);
  if (input.insuranceSalary === null) {
    insBase = dec(input.contractSalary);
    for (const it of sorted) if (it.def.countsForInsurance && it.def.autoSource !== 'contract_salary' && it.def.kind === 'earning') insBase = insBase.plus(it.value);
  }
  const insurance = computeInsurance(insBase, params, input.hasInsurance);

  const auto = new Map<string, number>();
  const results: PayrollItemResult[] = [];
  const pending: { r: PayrollItemResult; it: PayrollItemInput }[] = [];
  for (const it of sorted) {
    const d = it.def;
    let qty: number | null = null;
    let rate: number | null = null;
    let a = new D(0);
    const value = d.autoSource === 'contract_salary' ? input.contractSalary : it.value;
    if (d.autoSource === 'driver_ot') a = dec(input.driverOt);
    else if (d.autoSource === 'driver_late') a = dec(input.driverLate);
    else if (d.autoSource === 'driver_trip') a = dec(input.driverTrip ?? 0);
    else if (d.autoSource === 'insurance') a = dec(insurance.employee.total);
    else if (d.autoSource === 'salary_advance') a = dec(input.salaryAdvance ?? 0);
    else if (d.autoSource === 'bonus') a = dec(input.bonus ?? 0);
    else if (d.autoSource === 'fuel') a = dec(input.fuelSupport ?? 0);
    else if (d.autoSource === 'recovery') a = dec(input.recovery ?? 0);
    else if (d.autoSource === 'sunday' || d.autoSource === 'after_hours') {
      qty = d.autoSource === 'sunday' ? (input.sundayDays ?? 0) : (input.afterHours ?? 0);
      rate = value;
      a = dec(value).mul(qty);
    }
    else if (d.calc === 'fixed') a = dec(value);
    else if (d.calc === 'by_workday') {
      qty = input.workDays;
      rate = value;
      a = dec(value).mul(ratio);
    } else if (d.calc === 'by_hour') {
      qty = input.otHours;
      rate = value;
      a = dec(value).mul(input.otHours);
    } else if (d.calc === 'manual') a = dec(value);
    if (d.loseOnUnexcusedAbsence && loses(input)) a = new D(0);
    const r: PayrollItemResult = {
      itemId: d.itemId, code: d.code, name: d.name, kind: d.kind, calc: d.calc, autoSource: d.autoSource,
      taxable: d.taxable, taxExemptCap: d.taxExemptCap, sortOrder: d.sortOrder,
      qty, rate, autoAmount: roundVnd(a), overrideAmount: null, overrideReason: null, amount: 0,
    };
    if (d.calc === 'formula' && d.autoSource === null) pending.push({ r, it });
    else auto.set(d.code, r.autoAmount);
    results.push(r);
  }
  // Khoản công thức tính sau, theo SỐ TỰ TÍNH của các khoản gốc (như CRM: sửa tay LCB không kéo theo GTT)
  for (const { r, it } of pending) {
    const base = it.def.formulaBaseCodes.reduce((s, c) => s.plus(auto.get(c) ?? 0), new D(0));
    r.qty = null;
    r.rate = it.def.formulaRate;
    let a = base.mul(it.def.formulaRate ?? 0);
    if (it.def.loseOnUnexcusedAbsence && loses(input)) a = new D(0);
    r.autoAmount = roundVnd(a);
    auto.set(it.def.code, r.autoAmount);
  }
  for (let i = 0; i < results.length; i++) {
    const r = results[i]!;
    const it = sorted[i]!;
    if (it.override !== null && it.override !== undefined) {
      r.overrideAmount = roundVnd(it.override);
      r.overrideReason = it.overrideReason ?? null;
    }
    r.amount = r.overrideAmount ?? r.autoAmount;
  }
  const totals = computeTotals(results, {
    taxMode: input.taxMode,
    dependants: input.dependants,
    params,
    insuranceEmployee: insurance.employee.total,
    insuranceEmployer: insurance.employer.total,
  });
  return { ...totals, ratio: ratio.toDecimalPlaces(4).toNumber(), insuranceSalary: insurance.base, insurance, items: results };
}

/** Nội dung chuyển khoản không dấu: "Luong T10/2026 NV0001". */
export function bankTransferNote(month: string, employeeCode: string): string {
  return `Luong T${month.slice(5, 7)}/${month.slice(0, 4)} ${employeeCode}`;
}

/**
 * P21 PR 8 (DEC-272) – mã chi: nằm trong nội dung chuyển khoản và diễn giải phiếu chi Asia để đối soát 3 nguồn.
 * Lương tháng 10/2026 của NV0012: "MTL 1026 NV0012"; tạm ứng số 41: "MTU 0041 NV0012".
 */
export function payCode(kind: 'salary' | 'advance', ref: string | number, employeeCode: string): string {
  if (kind === 'salary') {
    const m = String(ref);
    return `MTL ${m.slice(5, 7)}${m.slice(2, 4)} ${employeeCode}`;
  }
  return `MTU ${String(ref).padStart(4, '0')} ${employeeCode}`;
}
/** Tìm mã chi trong nội dung chuyển khoản / diễn giải (ngân hàng hay thêm chữ trước / sau, đổi khoảng trắng). */
export function findPayCode(text: string | null | undefined): string | null {
  const m = /\bMT([LU])\s*(\d{4})\s*([A-Z]{1,6}\d{2,8})\b/i.exec(text ?? '');
  return m ? `MT${m[1]!.toUpperCase()} ${m[2]} ${m[3]!.toUpperCase()}` : null;
}
/** Tách tiền nhận: phần tiền mặt làm tròn tới 1.000 đ, còn lại chuyển khoản; không có số TK thì tiền mặt hết. */
export function splitPayout(amount: number, cashPercent: number, hasAccount: boolean): { bank: number; cash: number } {
  if (!hasAccount || cashPercent >= 100) return { bank: 0, cash: amount };
  if (cashPercent <= 0) return { bank: amount, cash: 0 };
  const cash = Math.min(amount, Math.round((amount * cashPercent) / 100 / 1000) * 1000);
  return { bank: amount - cash, cash };
}

/** Bỏ dấu tiếng Việt (tên trong file ngân hàng). */
export function stripVietnamese(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D');
}
